import { Job, Status } from '@api/apiClient';
import i18n from '@i18n/i18n';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';

interface JobReportProps {
  open: boolean;
  onClose: () => void;
  job: Job | null;
}

interface ParsedLogEntry {
  timestamp: string;
  message: string;
  isJson: boolean;
  jsonData?: any;
}

// Parse the job report field which contains timestamped logs
const parseJobReport = (report: string | null | undefined): ParsedLogEntry[] => {
  if (!report) {
    return [];
  }

  const entries: ParsedLogEntry[] = [];
  const lines = report.split('\n');
  
  let currentEntry: ParsedLogEntry | null = null;
  const timestampRegex = /^\[(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2})\]/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(timestampRegex);

    if (match) {
      // Save previous entry (but skip if it's a metadata JSON block)
      if (currentEntry && !shouldSkipEntry(currentEntry.message)) {
        // Trim trailing whitespace from the completed entry
        currentEntry.message = currentEntry.message.trimEnd();
        entries.push(currentEntry);
      }

      // Start new entry
      const timestamp = match[1];
      const message = line.substring(match[0].length).trim();
      currentEntry = {
        timestamp,
        message,
        isJson: false,
      };
    } else if (currentEntry) {
      // Continuation of previous message (including empty lines for proper formatting)
      currentEntry.message += '\n' + line;
    }
  }

  // Add last entry (but skip if it's a metadata JSON block)
  if (currentEntry && !shouldSkipEntry(currentEntry.message)) {
    // Trim trailing whitespace from the last entry
    currentEntry.message = currentEntry.message.trimEnd();
    entries.push(currentEntry);
  }

  return entries;
};

// Helper function to identify entries that should be skipped (metadata JSON blocks)
const shouldSkipEntry = (message: string): boolean => {
  const trimmedMessage = message.trim();
  
  // Skip entries that are just metadata labels followed by JSON
  // Common patterns: "Job information:", "Job Status:"
  if (trimmedMessage.startsWith('Job information:') || 
      trimmedMessage.startsWith('Job Status:')) {
    return true;
  }
  
  // Skip entries that start with just a JSON object (no real message)
  if (trimmedMessage.startsWith('{')) {
    return true;
  }
  
  return false;
};

// Calculate duration between two dates
const calculateDuration = (createdAt?: Date, completedAt?: Date): string => {
  if (!createdAt || !completedAt) {
    return 'N/A';
  }
  
  const start = new Date(createdAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  
  if (durationMs < 0) {
    return 'Invalid';
  }
  
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const JobReport: React.FC<JobReportProps> = ({ open, onClose, job }) => {
  const logEntries = useMemo(() => {
    if (!job?.report) {
      return [];
    }
    return parseJobReport(job.report);
  }, [job?.report]);

  if (!job) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t('job-report.title')}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>{i18n.t('job-report.no-data')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{i18n.t('job-report.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const getStatusColor = (status: string): "error" | "success" | "warning" | "default" => {
    switch (status) {
      case Status.Success:
        return 'success';
      case Status.FailedInstructions:
      case Status.FailedDownload:
      case Status.FailedUpload:
        return 'error';
      case Status.Inprogress:
      case Status.Pending:
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {i18n.t('job-report.title')}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Job Summary */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.filename')}</Typography>
              <Typography variant="body1">
                {job.apsOutputFile || i18n.t('job-report.na')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.type')}</Typography>
              <Typography variant="body1">{job.type}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.status')}</Typography>
              <Chip 
                label={job.status || i18n.t('job-report.status-unknown')} 
                color={getStatusColor(job.status || '')} 
                size="small" 
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.created-at')}</Typography>
              <Typography variant="body1">
                {job.createdAt ? new Date(job.createdAt).toLocaleString() : i18n.t('job-report.na')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.completed-at')}</Typography>
              <Typography variant="body1">
                {job.completedAt ? new Date(job.completedAt).toLocaleString() : i18n.t('job-report.na')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('job-report.duration')}</Typography>
              <Typography variant="body1">{calculateDuration(job.createdAt, job.completedAt)}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Job Log */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            {i18n.t('job-report.job-log', { count: logEntries.length })}
          </Typography>

          {logEntries.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '180px', fontWeight: 'bold' }}>{i18n.t('job-report.timestamp')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{i18n.t('job-report.message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logEntries.map((entry, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', verticalAlign: 'top' }}>
                        {entry.timestamp}
                      </TableCell>
                      <TableCell>
                        {entry.isJson ? (
                          <Box
                            component="pre"
                            sx={{
                              margin: 0,
                              padding: 1,
                              backgroundColor: 'grey.100',
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.85rem',
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {JSON.stringify(entry.jsonData, null, 2)}
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                            }}
                          >
                            {entry.message}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {i18n.t('job-report.no-log-entries')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{i18n.t('job-report.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobReport;

